import { Box, Text } from "@primer/react";
import { useEffect, useState } from "react";
import { useSaveDeadline } from "../hooks/useSaveDeadline";

interface ISaveCountdownProps {
    /** true = chiếm full chiều ngang (mobile, đặt trên nút). false (mặc định) = badge gọn cạnh nút. */
    block?: boolean;
    sx?: Record<string, unknown>;
}

/**
 * Bộ đếm ngược cảnh báo SV còn bao lâu để bấm "Lưu dữ liệu" trước khi hệ thống TỰ HUỶ kết quả đăng ký.
 * Tự đọc redux (xem useSaveDeadline) → tự ẩn khi đợt không bật tự huỷ / chưa đăng ký / đã chốt. Mốc
 * reset khi SV đăng ký thêm. Vàng (cảnh báo) → đỏ khi còn < 60s. Hết giờ → "đang huỷ".
 *
 * Dùng chung cho cả desktop (ClassesPage, /my) lẫn mobile (RegisteredTab).
 */
export const SaveCountdown = ({ block, sx }: ISaveCountdownProps) => {
    const deadlineMs = useSaveDeadline();
    const [now, setNow] = useState<number>(() => Date.now());

    useEffect(() => {
        if (deadlineMs == null) return;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [deadlineMs]);

    if (deadlineMs == null) return null;

    const remainMs = deadlineMs - now;
    const expired = remainMs <= 0;
    const totalSec = Math.max(0, Math.floor(remainMs / 1000));
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const pad = (n: number) => String(n).padStart(2, "0");
    const text = h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
    const critical = expired || totalSec <= 60;

    const c = critical
        ? { bg: "#fdecea", border: "#f5b5ae", fg: "#b42318" }
        : { bg: "#fff4e5", border: "#ffd9a8", fg: "#9a6700" };

    return (
        <Box
            role="status"
            aria-live="polite"
            sx={{
                display: block ? "flex" : "inline-flex",
                alignItems: "center",
                justifyContent: block ? "center" : "flex-start",
                width: block ? "100%" : "auto",
                gap: 2,
                bg: c.bg,
                border: "1px solid",
                borderColor: c.border,
                borderRadius: 2,
                px: "10px",
                py: "6px",
                ...sx,
            }}
        >
            <i
                className={`fas ${expired ? "fa-hourglass-end" : "fa-clock"}`}
                style={{ color: c.fg, fontSize: 14, flexShrink: 0 }}
            />
            <Text sx={{ fontSize: 1, fontWeight: 600, color: c.fg, lineHeight: 1.4 }}>
                {expired ? (
                    "Đang huỷ kết quả do quá hạn lưu…"
                ) : (
                    <>
                        Còn <b>{text}</b> để bấm "Lưu dữ liệu" — quá giờ kết quả đăng ký sẽ <b>tự động bị huỷ</b>.
                    </>
                )}
            </Text>
        </Box>
    );
};

export default SaveCountdown;
